import React, {useEffect, useRef} from 'react';
import {usePrefersReducedMotion} from '@packages/systems/spring-hooks';

import type {CSSProperties} from '@packages/utilities/css-properties-type';

type VideoProps = {
  srcMp4: string;
  altText: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  showControls?: 'onStart' | 'onFinish';
  poster?: string;
  style?: CSSProperties;
};

export const Video = ({
  srcMp4,
  altText,
  showControls,
  autoPlay = true,
  loop = false,
  muted = true,
  poster = '',
  style = {},
}: VideoProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const previousUrl = useRef(srcMp4);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.addEventListener('mouseover', addControlsAttribute);
      video.addEventListener('focus', addControlsAttribute);

      if (showControls === 'onFinish') {
        video.addEventListener('ended', addControlsAttribute);
      }
    }
  }, []);

  useEffect(() => {
    if (previousUrl.current === srcMp4) {
      return;
    }

    if (videoRef.current) {
      videoRef.current.load();
    }

    previousUrl.current = srcMp4;
  }, [srcMp4]);

  const addControlsAttribute = () => {
    const video = videoRef.current;

    if (video && !video.hasAttribute('controls')) {
      video.setAttribute('controls', 'true');
      video.setAttribute('disablepictureinpicture', 'true');
      video.setAttribute(
        'controlsList',
        'nodownload noremoteplayback nofullscreen'
      );
    }
  };

  return (
    // TODO: fix jsx-a11y lint error
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      width="100%"
      height="auto"
      controls={showControls === 'onStart'}
      autoPlay={autoPlay && !prefersReducedMotion}
      loop={loop}
      ref={videoRef}
      muted={muted}
      title={altText}
      poster={poster}
      // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number'.
      tabIndex="0"
      style={style}
      data-legacy-ds="Video"
    >
      <source src={srcMp4} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
};
